<!-- Garage — the car you drive now, and what is coming next: more cars to
     choose from and the pit lane. -->
<template>
  <IonPage>
    <AppHeader :title="COPY.title" :back="backTo" />

    <IonContent :fullscreen="true">
      <div class="ion-padding garage-body">
        <SurfaceCard v-if="storeError" :title="COPY.errorTitle" :body="COPY.errorBody" />

        <SectionHeader :title="COPY.readyHeading" />
        <EmptyState v-if="!ready.length" :icon="carSportOutline" :title="COPY.emptyTitle" :body="COPY.emptyBody" />
        <article v-for="car in ready" :key="car.id" class="car car--ready">
          <GridScene :label="COPY.readyArt" class="car-art" />
          <div class="car-text">
            <h2 class="car-name">{{ car.name }}</h2>
            <p class="car-sub">{{ car.summary }}</p>
          </div>
          <StatusPill :label="COPY.ready" tone="success" />
        </article>
        <div v-if="best" class="garage-stats">
          <StatTile :label="COPY.bestLabel" :value="formatLapTime(best.timeMs)" />
          <StatTile :label="COPY.lapsLabel" :value="finishedCount" />
        </div>

        <SectionHeader :title="COPY.nextHeading" />
        <IonList :inset="true" class="kit-list">
          <ListRow
            v-for="car in comingNext"
            :key="car.id"
            :title="car.name"
            :secondary="car.summary"
            :icon="car.name === PIT_LANE ? constructOutline : carSportOutline"
          />
        </IonList>

        <ActionButton :label="COPY.start" kind="primary" data-cta="start-btn" @click="toGrid" />
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { IonPage, IonContent, IonList } from '@ionic/vue';
import { carSportOutline, constructOutline } from 'ionicons/icons';
import { AppHeader, SectionHeader, ListRow, StatusPill, StatTile, SurfaceCard, EmptyState, ActionButton } from '../core/ui/index.js';
import GridScene from '../components/GridScene.vue';
import { sampleCars, sampleCarsReady, addSampleCar, laps, storeError } from '../store.js';
import { bestLap, CAR } from '../game/results.js';
import { formatLapTime } from '../game/race.js';

const PIT_LANE = 'Pit lane and tyres';
const COPY = {
  title: 'Garage',
  readyHeading: 'Ready to drive',
  readyArt: 'The red Lamborghini Huracan on the grid',
  ready: 'Ready',
  nextHeading: 'Coming next',
  bestLabel: 'Best lap',
  lapsLabel: 'Laps finished',
  start: 'Start lap',
  emptyTitle: 'No car in the garage',
  emptyBody: 'The garage fills itself the first time it opens.',
  errorTitle: 'Not saved on this device',
  errorBody: 'The garage could not be saved on this device. You can still race.',
};

// The garage starts with the one car you can drive and what comes after it.
const GARAGE = [
  { name: CAR, summary: 'Red, number 1. Yours for every lap of Marina Bay.', status: 'ready' },
  { name: 'Blue V8 supercar', summary: 'Choose your car, in the next version.', status: 'coming-next' },
  { name: 'Yellow V8 supercar', summary: 'Choose your car, in the next version.', status: 'coming-next' },
  { name: PIT_LANE, summary: 'Pull into the pits and change tyres mid-race.', status: 'coming-next' },
];
sampleCarsReady.then((rows) => {
  if (rows.some((r) => Number(r.id) < 0)) return;   // /demo rows
  for (const car of GARAGE) if (!rows.some((r) => r.name === car.name)) addSampleCar(car);
});

const ready = computed(() => sampleCars.value.filter((c) => c.status === 'ready'));
const comingNext = computed(() => sampleCars.value.filter((c) => c.status === 'coming-next'));
const best = computed(() => bestLap.value);
const finishedCount = computed(() => laps.value.filter((l) => Number(l.timeMs) > 0).length);

const router = useRouter();
const backTo = router.hasRoute('grid') ? router.resolve({ name: 'grid' }).fullPath : '/';
const toGrid = () => router.replace({ name: 'grid' });
</script>

<style scoped>
.garage-body { display: grid; gap: var(--sp-3); max-width: 560px; margin: 0 auto; }
.car { display: grid; gap: var(--sp-3); padding: var(--sp-3); border-radius: var(--radius); background: var(--ion-item-background, var(--ion-background-color)); border: 1px solid var(--ion-border-color, rgba(0,0,0,0.12)); }
.car-name { margin: 0; font-size: var(--text-lg); font-weight: 800; color: var(--ink); }
.car-sub { margin: 4px 0 0; color: var(--dim); }
.car :deep(.kit-pill), .car > :last-child { justify-self: start; }
/* The body already carries the page gutter, so the kit's section header and
 * inset list line up with the car card instead of indenting twice. */
.garage-body :deep(.kit-section) { padding-inline: var(--sp-1); }
.garage-body :deep(.kit-list) { margin-inline: 0; }
.garage-stats { display: grid; grid-template-columns: 1fr 1fr; gap: var(--sp-3); }
</style>
